import OutOfWordsModal from "components/layout/OutOfWordsModal";
import { CompanyContext } from "contexts/CompanyContext";
import { ModalContext } from "contexts/ModalContext";
import { SessionContext } from "contexts/SessionContext";
import { Configuration, OpenAIApi } from "openai";
import { useContext, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { api as Api } from "api/http";
import { replaceAll } from "utils/replaceAll";

const THE_NEWS_API_SUPPORTED_LANGUAGES = ["ar","bg","bn","cs","da","de","el","en","es","et","fa","fi","fr","he","hi","hr","hu","id","it","ja","ko","lt","multi","nl","no","pl","pt","ro","ru","sk","sv","ta","th","tr","uk","vi","zh"]; 
const THE_NEWS_API_SUPPORTED_MKTS = ["es-AR", "en-AU", "de-AT", "nl-BE", "fr-BE", "pt-BR", "en-CA", "fr-CA", "es-CL", "da-DK", "fi-FI", "fr-FR", "de-DE", "zh-HK", "en-IN", "en-ID", "it-IT", "ja-JP", "ko-KR", "en-MY", "es-MX", "nl-NL", "en-NZ", "no-NO", "zh-CN", "pl-PL", "en-PH", "ru-RU", "en-ZA", "es-ES", "sv-SE", "fr-CH", "de-CH", "zh-TW", "tr-TR", "en-GB", "en-US", "es-US"];

const THE_NEWS_API_COUNTRY_MAPPING = {
    "ar": "ar",
    "am": "am",
    "au": "au",
    "at": "at",
    "by": "by",
    "be": "be",
    "bo": "bo",
    "br": "br",
    "bg": "bg",
    "ca": "ca",
    "cl": "cl",
    "cn": "cn",
    "co": "co",
    "hr": "hr",
    "cz": "cz",
    "ec": "ec",
    "eg": "eg",
    "fr": "fr",
    "de": "de",
    "gr": "gr",
    "hn": "hn",
    "hk": "hk",
    "in": "in",
    "id": "id",
    "ir": "ir",
    "ie": "ie",
    "il": "il",
    "it": "it",
    "jp": "jp",
    "kr": "kr",
    "mx": "mx",
    "nl": "nl",
    "nz": "nz",
    "ni": "ni",
    "pk": "pk",
    "pa": "pa",
    "pe": "pe",
    "pl": "pl",
    "pt": "pt",
    "qa": "qa",
    "ro": "ro",
    "ru": "ru",
    "sa": "sa",
    "za": "za",
    "es": "es",
    "ch": "ch",
    "sy": "sy",
    "tw": "tw",
    "th": "th",
    "tr": "tr",
    "ua": "ua",
    "gb": "gb",
    "us": "us",
    "uy": "uy",
    "ve": "ve"
};

export const useTrendingNews = () => {

    const { company } = useContext(CompanyContext);

    const getTrendingNewsBing = useCallback(async (
        query = "", 
        dateFrom = new Date(new Date().getTime() - 3600 * 24 * 30 * 1000), 
        dateTo = new Date(new Date().getTime() + 1000 * 60 * 2),
        sortBy = "date", 
        language = undefined,
        searchIn = "title" 
    ) => {

        if (sortBy == 'publishedAt') sortBy = 'date';
        if (sortBy == 'relevancy') sortBy = 'relevance';
        if (sortBy == 'popularity') sortBy = 'relevance';
        
        language = !language ? THE_NEWS_API_SUPPORTED_MKTS?.filter(e => e.split("-")[1].toLowerCase().includes(company?.language_iso_code?.toLowerCase()))?.[0] : language;
        language = THE_NEWS_API_SUPPORTED_MKTS.includes(language) ? language : "en-US";

        const interests = company?.interests?.sort(() => 0.5 - Math.random());
        interests?.map((interest) => {
            if (interest?.length >= 3 && interest?.length < 25 && !query) {
                query = interest;
            }
        });
        
        const response = await Api.get(`/news/bing?q=${query}&sortBy=${sortBy}&mkt=${language}&count=60&originalImg=1`, {}).then((response) => response);

        return response?.data?.map((item) => {
            return {
                urlToImage: item?.image?.contentUrl || replaceAll(item?.image?.thumbnail?.contentUrl, "&pid=News", ""),
                title: item?.name,
                description: item?.description,
                source: { name: item?.provider?.[0]?.name },
                url: item?.url
            }
        });
    }, [company]);
    
    const getTrendingNewsConnexun = useCallback(async (
        query = "", 
        dateFrom = new Date(new Date().getTime() - 3600 * 24 * 30 * 1000), 
        dateTo = new Date(new Date().getTime() + 1000 * 60 * 2),
        sortBy = "published", 
        language = undefined,
        searchIn = "title" 
    ) => {

        if (sortBy == 'publishedAt') sortBy = 'published';
        if (sortBy == 'relevancy') sortBy = 'relevance';
        if (sortBy == 'popularity') sortBy = 'relevance';

        if (query?.length > 25) query?.substring(0, 25);
        
        language = !language ? company?.language_iso_code : language;
        language = THE_NEWS_API_SUPPORTED_LANGUAGES.includes(language) ? language : "en";

        dateFrom = new Date(dateFrom).toISOString();
        dateTo = new Date(dateTo).toISOString();

        const interests = company?.interests?.sort(() => 0.5 - Math.random());
        interests.map((interest) => {
            if (interest?.length >= 3 && interest?.length < 25 && !query) {
                query = interest;
            }
        });
        
        const response = await Api.get(`/news/connexun?qField=${searchIn}&search=${query}&sortBy=${sortBy}&searchType=OR&languages=${language}&publishedAfter=${dateFrom}&batchSize=30`, {}).then((response) => response);

        return response?.data?.map((item) => {
            return {
                urlToImage: item?.Image,
                title: item?.Title,
                description: item?.Description,
                source: { name: item.Source },
                url: item?.Url
            }
        });
    }, [company]);
    
    const getTrendingNewsTheNewsAPI = useCallback(async (
        query = "", 
        dateFrom = new Date(new Date().getTime() - 3600 * 24 * 30 * 1000), 
        dateTo = new Date(new Date().getTime() + 1000 * 60 * 2),
        sortBy = "published_at", // published_at, relevance_score
        language = undefined,
        searchIn = "title" // possible values: title, description, content
    ) => {

        if (sortBy == 'publishedAt') sortBy = 'published_at';
        if (sortBy == 'relevancy') sortBy = 'relevance_score';
        if (sortBy == 'popularity') sortBy = 'relevance_score';
        if (sortBy == 'date') sortBy = 'published_at';
        if (sortBy == 'published') sortBy = 'published_at';

        // Build query parameters
        const params = new URLSearchParams();
        
        // Search query and fields
        if (query) {
            params.append('search', query);
            params.append('search_fields', searchIn);
        }
        
        // Extract country code from language parameter (e.g., "en-US" -> "us")
        let countryCode = null;
        if (language && language.includes('-')) {
            const countryPart = language.split('-')[1]?.toLowerCase();
            if (THE_NEWS_API_COUNTRY_MAPPING[countryPart]) {
                countryCode = THE_NEWS_API_COUNTRY_MAPPING[countryPart];
            }
        }
        
        // If no country found from language, try to get from company language
        if (!countryCode && company?.language_iso_code) {
            const companyCountryPart = company.language_iso_code.split('-')?.[1]?.toLowerCase();
            if (THE_NEWS_API_COUNTRY_MAPPING[companyCountryPart]) {
                countryCode = THE_NEWS_API_COUNTRY_MAPPING[companyCountryPart];
            }
        }
        
        // Add country parameter if available
        if (countryCode) {
            params.append('locale', countryCode);
        }
        
        // Language and Country
        language = !language ? company?.language_iso_code : language;
        language = THE_NEWS_API_SUPPORTED_LANGUAGES.includes((language || "")?.toLowerCase()) ? language : "en";
        // params.append('language', language);
        
        // Date filtering
        if (dateFrom) {
            params.append('published_after', new Date(dateFrom).toISOString().split('T')[0]);
        }
        if (dateTo) {
            params.append('published_before', new Date(dateTo).toISOString().split('T')[0]);
        }
        
        // Sorting and pagination
        params.append('sort', sortBy);
        params.append('limit', '30');
        params.append('page', '1');

        // If no query provided, use company interests
        // if (!query) {
        //     const interests = company?.interests?.sort(() => 0.5 - Math.random());
        //     const selectedInterest = interests?.find(interest => 
        //         interest?.length >= 3 && interest?.length < 100
        //     );
        //     if (selectedInterest) {
        //         params.set('search', selectedInterest);
        //         params.set('search_fields', searchIn);
        //     }
        // }
        
        const response = await Api.get(`/news/thenewsapi?${params.toString()}`, {}).then((response) => response);

        return response?.data?.map((item) => {
            return {
                urlToImage: item?.image_url,
                title: item?.title,
                description: item?.description || item?.snippet,
                source: { name: item?.source },
                url: item?.url,
                publishedAt: item?.published_at
            }
        });
    }, [company]);

    const getTrendingNewsNewsAPI = useCallback(async (
        query = "", 
        dateFrom = new Date(new Date().getTime() - 3600 * 24 * 30 * 1000), 
        dateTo = new Date(),
        sortBy = "publishedAt", // relevancy, popularity, publishedAt
        language = undefined,
        searchIn = "title,description,content" // possible values: title, description, content
    ) => {
        
        language = !language ? company?.language_iso_code : language;
        language = THE_NEWS_API_SUPPORTED_LANGUAGES.includes(language) ? language : "en";

        dateFrom = new Date(dateFrom).toISOString().split('T')[0];
        dateTo = new Date(dateTo).toISOString().split('T')[0];

        const interests = company?.interests?.sort(() => 0.5 - Math.random());
        const chunkSize = 4;
        let defaultQueryArrays = [];
        for (let i = 0; i < interests?.length; i += chunkSize) {
            defaultQueryArrays.push(interests.slice(i, i + chunkSize).join(" AND "));
        }

        query = query ? query : `(${defaultQueryArrays.join(") OR (")})`;
        
        const response = await Api.get(`/news?q=${query}&sortBy=${sortBy}&searchIn=${searchIn}&language=${language}&from=${dateFrom}&to=${dateTo}&pageSize=25`, {}).then((response) => response);
        
        return response?.data;
    }, [company]);

    return useMemo(() => ({ 
        getTrendingNewsBing, 
        getTrendingNewsConnexun, 
        getTrendingNews: getTrendingNewsTheNewsAPI, 
        getTrendingNewsNewsAPI 
    }), [getTrendingNewsBing, getTrendingNewsConnexun, getTrendingNewsTheNewsAPI, getTrendingNewsNewsAPI]);
}

